import { useEffect, useRef, useState, useCallback } from 'react';
import { Client, IFrame, IMessage } from '@stomp/stompjs';
import { WS_BACKEND_URL } from '../lib/backendUrl';

const WS_URL = `${WS_BACKEND_URL}/ws`;

export const useWebSocket = () => {
  const clientRef = useRef<Client | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const pendingSubscriptionsRef = useRef<Array<{ topic: string; callback: (data: any) => void }>>([]);
  const activeSubscriptionsRef = useRef<Map<string, () => void>>(new Map());

  useEffect(() => {
    const client = new Client({
      brokerURL: WS_URL,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      debug: (str: string) => {
        // Uncomment for debugging: console.log('[STOMP]', str);
      },
      onConnect: () => {
        console.log('STOMP connected to', WS_URL);
        setIsConnected(true);

        // Process any pending subscriptions
        pendingSubscriptionsRef.current.forEach(({ topic, callback }) => {
          doSubscribe(topic, callback);
        });
        pendingSubscriptionsRef.current = [];
      },
      onDisconnect: () => {
        console.log('STOMP disconnected');
        setIsConnected(false);
      },
      onStompError: (frame: IFrame) => {
        console.error('STOMP error:', frame.headers['message'], frame.body);
        setIsConnected(false);
      },
    });

    client.activate();
    clientRef.current = client;

    return () => {
      activeSubscriptionsRef.current.forEach((unsub) => unsub());
      activeSubscriptionsRef.current.clear();
      pendingSubscriptionsRef.current = [];
      client.deactivate();
      clientRef.current = null;
    };
  }, []);

  const doSubscribe = (topic: string, callback: (data: any) => void) => {
    if (!clientRef.current || !clientRef.current.connected) return () => {};

    const subscription = clientRef.current.subscribe(topic, (message: IMessage) => {
      try {
        const data = JSON.parse(message.body);
        callback(data);
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    });

    activeSubscriptionsRef.current.set(topic, () => subscription.unsubscribe());
    return () => {
      subscription.unsubscribe();
      activeSubscriptionsRef.current.delete(topic);
    };
  };

  const subscribe = useCallback((topic: string, callback: (data: any) => void) => {
    if (!clientRef.current || !clientRef.current.connected) {
      // Queue subscription until connection is established
      console.log('STOMP not connected yet, queuing subscription to', topic);
      pendingSubscriptionsRef.current.push({ topic, callback });
      return () => {
        pendingSubscriptionsRef.current = pendingSubscriptionsRef.current.filter(
          (sub) => sub.topic !== topic
        );
      };
    }

    return doSubscribe(topic, callback);
  }, []);

  return { client: clientRef.current, isConnected, subscribe };
};
